import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Modal,
  Grid,
  Button,
} from "@mui/material";
import { Link } from "react-router-dom";
import "../assets/css/icon.css";

const VARIAZIONE_INFO_TEXT = `Poiché il numero di campi valutabili può cambiare anno dopo anno (es. i campi valutati come "non applicabili" non vengono conteggiati nella singola valutazione), i punteggi vengono convertiti in percentuale prima di confrontarli. Esempio:

anno 1: punteggio di 90 su 120 = 75%
anno 2: punteggio di 75 su 91 = 82,42%
Variazione: +9,89%
In questo modo si confronta la performance reale, non numeri grezzi su scala diversa.`;

const parseScoreFraction = (scoreStr) => {
  if (!scoreStr || typeof scoreStr !== "string") return null;
  const [value, max] = scoreStr.split("/").map(Number);
  if (!max || Number.isNaN(value) || Number.isNaN(max)) return null;
  return { value, max };
};

const calcolaVariazionePercentuale = (
  punteggio_corrente,
  denominatore_corrente,
  punteggio_precedente,
  denominatore_precedente
) => {
  const percentuale_corrente = (punteggio_corrente / denominatore_corrente) * 100;
  const percentuale_precedente =
    (punteggio_precedente / denominatore_precedente) * 100;

  const variazione =
    ((percentuale_corrente - percentuale_precedente) / percentuale_precedente) *
    100;

  return {
    percentuale_corrente: percentuale_corrente.toFixed(2),
    percentuale_precedente: percentuale_precedente.toFixed(2),
    variazione_percentuale: variazione.toFixed(2),
  };
};

const getDifferenzaPartner = (item, evaluations) => {
  const currentScore = parseScoreFraction(
    item.data_partner?.score_valutation_partner
  );
  if (!currentScore) return null;

  const collaboratorId = item.collaborator_id ?? item.collaborator?.id;
  const currentYear = Number(item.year);

  const previousEvaluation = evaluations
    .filter(
      (e) =>
        (e.collaborator_id ?? e.collaborator?.id) === collaboratorId &&
        Number(e.year) < currentYear &&
        parseScoreFraction(e.data_partner?.score_valutation_partner)
    )
    .sort((a, b) => Number(b.year) - Number(a.year))[0];

  if (!previousEvaluation) return null;

  const previousScore = parseScoreFraction(
    previousEvaluation.data_partner?.score_valutation_partner
  );
  if (!previousScore || previousScore.value === 0) return null;

  const { variazione_percentuale } = calcolaVariazionePercentuale(
    currentScore.value,
    currentScore.max,
    previousScore.value,
    previousScore.max
  );

  const numero = Number(variazione_percentuale);
  const segno = numero > 0 ? "+" : "";
  return `${segno}${variazione_percentuale}%`;
};

const ValuoTable = ({ evaluations, user }) => {
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const intestazioni = [
    { key: "anno", label: "Anno" },
    { key: "status", label: "Stato" },
    { key: "professionista", label: "Professionista" },
    { key: "autovalutazione", label: "Autovalutazione Professionista" },
    { key: "valutazionePartner", label: "Valutazione Partner" },
    { key: "differenza", label: "Δ Precedente" },
    { key: "dettaglio", label: "Dettaglio" },
  ];

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{ overflowX: "auto", borderRadius: 0, boxShadow: "none" }}
      >
        <Table size="medium" sx={{ width: "100%", minWidth: "600px" }}>
          <TableHead>
            <TableRow className="bg-blue-dark">
              {intestazioni.map((col) => (
                <TableCell
                  key={col.key}
                  sx={{
                    color: "#fff",
                    fontWeight: 600,
                    textAlign: "center",
                    borderRight: "1px solid #fff",
                    fontSize: "15px",
                  }}
                >
                  <Typography
                    className="color-light"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                    }}
                  >
                    {col.label}
                    {col.key === "differenza" && (
                      <span
                        className="icon-modal-info-small"
                        onClick={() => setInfoModalOpen(true)}
                        style={{
                          cursor: "pointer",
                          filter: "brightness(0) invert(1)",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {evaluations.map((item) => {
              const phase = Number(item.phase);

              const scoreCollaborator =
                item.data_collaborator?.score_valutation_collaborator ?? "-";

              const scorePartner =
                item.data_partner?.score_valutation_partner ?? "-";

              const showCollaboratorScore = () => {
                if (phase === 0) return user.role === "collaborator";
                if (phase === 1) return user.role === "partner" ? false : true;
                if (phase >= 2) return true;
                return false;
              };

              const showPartnerScore = () => {
                if (phase === 0) return false;
                if (phase === 1) return user.role === "partner";
                if (phase >= 2) return true;
                return false;
              };

              const rowKey = `${item.anno}-${item.mese}-${item.id}`;

              const differenzaPartner = getDifferenzaPartner(item, evaluations);

              return (
                <TableRow
                  key={rowKey}
                  sx={{
                    backgroundColor:
                      (user?.role === "collaborator" && phase === 0) ||
                        (user?.role === "partner" && (phase === 1 || phase === 2))
                        ? "#fae63436"
                        : null,
                  }}
                >
                  <TableCell sx={{ textAlign: "center" }}>
                    <Typography>{item.year}</Typography>
                  </TableCell>

                  <TableCell sx={{ textAlign: "center" }}>
                    <Typography>
                      {phase === 0
                        ? "Compilazione professionista"
                        : phase === 1
                          ? "Compilazione partner"
                          : phase === 2
                            ? "Revisione congiunta"
                            : "Completata"}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ textAlign: "center" }}>
                    <Typography>
                      {user.role === "collaborator"
                        ? user.name
                        : item?.collaborator?.name}
                    </Typography>
                  </TableCell>

                  {/* COLLABORATOR SCORE */}
                  <TableCell sx={{ textAlign: "center" }}>
                    <Typography>
                      {showCollaboratorScore() ? scoreCollaborator : "-"}
                    </Typography>
                  </TableCell>

                  {/* PARTNER SCORE */}
                  <TableCell sx={{ textAlign: "center" }}>
                    <Typography>
                      {showPartnerScore() ? scorePartner : "-"}
                    </Typography>
                  </TableCell>

                  <TableCell
                    sx={{
                      textAlign: "center",
                      color: differenzaPartner?.startsWith("+")
                        ? "green"
                        : differenzaPartner?.startsWith("-")
                          ? "red"
                          : "inherit",
                    }}
                  >
                    <Typography>{differenzaPartner ?? "-"}</Typography>
                  </TableCell>

                  <TableCell sx={{ textAlign: "center" }}>
                    {(user.role === "collaborator" ||
                      user.role === "partner" ||
                      user.role === "superadmin") && (
                        <Link
                          to={`/evaluation/${item.id}`}
                          className="color-purple"
                          style={{
                            fontSize: "16px",
                            textDecoration: "underline",
                            fontWeight: 500,
                          }}
                        >
                          Vedi
                        </Link>
                      )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Modal open={infoModalOpen} onClose={() => setInfoModalOpen(false)}>
        <Grid
          container
          item
          xs={11}
          md={5}
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#fff",
            maxHeight: "90vh",
            overflowY: "auto",
            outline: "none",
          }}
        >
          <Grid
            item
            xs={12}
            sx={{
              minHeight: "60px",
              width: "100%",
              display: "flex",
              flexWrap: "nowrap",
              alignItems: "center",
              borderBottom: "1px solid #c6c6c6",
              px: "15px",
              py: 1,
              gap: 1.5,
            }}
          >
            <div className="icon-modal-info" style={{ flexShrink: 0 }} />
            <Typography
              className="color-blue-dark"
              sx={{
                fontSize: { xs: "16px", sm: "18px" },
                fontWeight: 600,
                flex: 1,
                minWidth: 0,
              }}
            >
              Calcolo automatico della variazione
            </Typography>
            <div
              onClick={() => setInfoModalOpen(false)}
              className="icon-modal-close"
              style={{ cursor: "pointer", flexShrink: 0 }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography
              className="color-blue-dark"
              sx={{
                padding: "20px 15px",
                whiteSpace: "pre-line",
              }}
            >
              {VARIAZIONE_INFO_TEXT}
            </Typography>
          </Grid>

          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              p: 2,
            }}
          >
            <Button
              variant="contained"
              className="button-bg-purple"
              onClick={() => setInfoModalOpen(false)}
              style={{ boxShadow: "none" }}
            >
              <Typography
                className="color-light"
                sx={{
                  textTransform: "initial",
                  fontSize: { xs: "16px", sm: "18px" },
                  fontWeight: 500,
                }}
              >
                Ho capito
              </Typography>
            </Button>
          </Grid>
        </Grid>
      </Modal>
    </>
  );
};

export default ValuoTable;
